import { STATIC } from './staticMode';
import { BUILD_ID } from './buildId';
import { MEDIA_ID, DICT_ID } from './mediaId';

// Backend base. In the static build there is no backend: API_BASE is '' and the
// only thing still served from our own origin is the app shell itself.
// In local dev, point at the LAN backend (so other devices reach the host machine).
export const API_BASE = STATIC ? '' : `http://${window.location.hostname}:8000`;

// Where the clips, waveforms, covers and language icons live.
//
// The static build reads them straight from Cloudflare R2 — 147 MB that used to
// ride inside every deploy. Direct, not proxied through Netlify: <audio> and
// <img> never trigger CORS at all, the bucket carries a CORS policy for the
// fetches that do (scripts/r2_cors.json), and going direct keeps 147 MB per
// cold visitor off Netlify's bandwidth. Public bucket URL, not a secret — the
// write credentials are in .env.r2 and never reach the browser.
//
// Dev keeps serving media off the FastAPI backend, so nothing about the local
// edit-and-listen loop changes: re-cut a clip and reload.
const R2_BASE = 'https://pub-d0f7b0e564684a23bd7a46ecc4e2b424.r2.dev';
export const MEDIA_BASE = STATIC ? R2_BASE : API_BASE;

// Static assets that are NOT audio — currently the language icons.
//
// These need their own base because MEDIA_BASE's dev value is the backend, and
// the backend serves exactly two media routes: /audio/{source}/{file} and
// /waveform/... (the live renderer). There is no /langicons, so sending icons to
// MEDIA_BASE 404s every icon in the library under `npm start` while looking
// perfectly fine in the deployed build. In dev they stay relative and come off
// the CRA dev server out of public/; in the static build they come from the
// bucket like everything else.
//
// Waveforms need no equivalent: SentenceView only ever requests the
// pre-generated PNGs under STATIC, and falls back to the backend's live
// renderer in dev, so that path never reaches for a bundled file.
export const ASSET_BASE = STATIC ? R2_BASE : '';

// Cache-buster for media URLs.
//
// This used to be `?v=${BUILD_ID}` on assets served with max-age=31536000 — a
// year-long cache behind a stamp that changed every deploy, so the cache never
// survived one and every device re-downloaded all 139 MB each time we shipped a
// one-line fix. The stamp could not just be removed, though: clips get re-cut
// under the same filename, and this was the only thing that made a re-cut
// visible to a browser holding the old bytes.
//
// So the stamp stays and its input changed. MEDIA_ID is a digest of the media
// itself, written by scripts/r2_push.py — it does not move when code changes,
// and it does move when a clip does. Dev is unchanged: the backend serves files
// that change under you, so it stays on the per-build stamp.
export const MEDIA_V = STATIC ? `?v=${MEDIA_ID}` : `?v=${BUILD_ID}`;

// The dictionaries: 10.1 MB across 32 files that used to be baked into the JS
// bundle by 34 static imports in dict.js. They are needed only when a word is
// tapped, and only ever for ONE dialect, so a Truku reader was downloading the
// dictionaries for fifteen other languages before the library list could render.
// dict.js now fetches them per dialect and caches them.
//
// Separate stamp from MEDIA_V on purpose: the dicts are hand-curated and change
// often, the audio is effectively frozen. Sharing one digest would mean fixing a
// single Thao gloss re-downloaded all 147 MB of clips. See stamp_media_id in
// scripts/r2_push.py.
export const DICT_BASE = STATIC ? `${R2_BASE}/dicts` : '';
export const DICT_V = STATIC ? `?v=${DICT_ID}` : `?v=${BUILD_ID}`;

// The library: the manifest, the sixteen books, and the review screen's
// word→dialect table. Baked by scripts/gen_library.mjs, published by r2_push.
//
// Deliberately NOT gated on STATIC — dev reads this from the bucket too, and that
// is the point. Every other base here forks on STATIC, and the last time one did
// (langicons pointed at MEDIA_BASE) it 404'd every icon under `npm start` while
// looking perfect deployed, because dev and prod were exercising different code.
// The library is the app's whole content path; it is the last thing that should
// only be tested in production. r2_cors.json already allows http://localhost:3000.
//
// The cost is real and worth naming: editing a story now means bake + push before
// dev shows it, instead of just saving the file. Clips already worked that way.
export const LIBRARY_BASE = `${R2_BASE}/library`;

// NOTE: there is deliberately no `ASSET_V = ?v=${BUILD_ID}` export any more.
// That was the bug — a per-deploy stamp on assets served max-age=31536000, so
// the year-long cache never survived a single deploy. It now has no consumers,
// and leaving it exported would just be a loaded footgun for the next asset
// someone adds. Use MEDIA_V.

export async function api(path, { method = 'GET', token, body } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['X-User-Token'] = token;
  const res = await fetch(API_BASE + path, {
    method, headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { const j = await res.json(); msg = j.detail || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}
